'use client';

/**
 * Apps / Agents views with the `Yours · Explore` segment (the marketplace
 * absorption, rail-redesign P2). "Yours" is the user's own library (the
 * existing AppsTab / AgentsTab); "Explore" browses the PUBLIC marketplace for
 * that domain — there is no separate Discover surface anymore.
 *
 * Explore browses the marketplace CHANNEL trees: apps = `//apps/<category>/*`,
 * agents = `//agents/<category>/*` (each listing is a channel, see
 * /api/marketplace/channels). Category chips are the real category subchannels.
 * Apps add a widget instance to the canvas via `addWidget(primaryContentId)`;
 * agents open their channel page (the app-store detail with the Chat CTA).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { BadgeCheck, AppWindow, ChevronLeft, MessageCircle, Grid2x2Plus } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';
import { Spinner } from '@/components/ui/spinner';
import { Avatar } from '@/components/avatar';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { useWorkspaceActions } from '@/components/workspace/store';
import { coverGradient } from '@/lib/marketplace/cover';
import { usePanelBackStack } from './use-panel-back';
import { ChannelPanelView } from '@/components/marketplace/channel-panel-view';
import { AppsTab } from './apps-tab';
import { AgentsTab } from './library-tabs';
import { FilterRail, type FilterChip } from './filter-rail';
import { panelSegmentBtn, PANEL_TABS_ROW } from './panel-ui';
import { useViewMode, ViewModeToggle, cardGridClass, cardGridStyle } from './view-mode';

function Segment({
  value,
  onChange,
  yoursLabel,
}: {
  value: 'yours' | 'explore';
  onChange: (v: 'yours' | 'explore') => void;
  yoursLabel: string;
}) {
  // Explore first (discovery-forward), then the user's own library.
  const items = [
    { id: 'explore' as const, label: 'Explore' },
    { id: 'yours' as const, label: yoursLabel },
  ];
  return (
    <div className={`${PANEL_TABS_ROW} px-2 pt-1.5`}>
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          className={panelSegmentBtn(value === it.id)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export function AppsView() {
  const [scope, setScope] = useState<'yours' | 'explore'>('explore');
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Segment value={scope} onChange={setScope} yoursLabel="My Apps" />
      {scope === 'yours' ? <AppsTab /> : <ExploreList kind="widget" />}
    </div>
  );
}

export function AgentsView() {
  const [scope, setScope] = useState<'yours' | 'explore'>('explore');
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Segment value={scope} onChange={setScope} yoursLabel="My Agents" />
      {scope === 'yours' ? <AgentsTab /> : <ExploreList kind="agent" />}
    </div>
  );
}

// A marketplace listing channel (//apps/<cat>/<app> or //agents/<cat>/<agent>).
type MarketChannel = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  color?: string | null;
  iconEmoji?: string | null;
  verificationStatus?: string | null;
  primaryContentId?: string | null;
  primaryAgentId?: string | null;
  category?: string | null;
};
type MarketCategory = { slug: string; name: string; color?: string | null; iconEmoji?: string | null };

function ExploreList({ kind }: { kind: 'widget' | 'agent' }) {
  const t = useT();
  const root = kind === 'widget' ? 'apps' : 'agents';
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [items, setItems] = useState<MarketChannel[]>([]);
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  // Native in-panel drill-down (a STACK, like the Channels tab): a listing
  // click pushes its ChannelPanelView; drilling a subchannel pushes another
  // level, and browser Back pops one level at a time.
  const [drill, setDrill] = useState<{ slug: string; name: string }[]>([]);
  const detail = drill[drill.length - 1] ?? null;
  const setDetail = (ch: MarketChannel | null) =>
    setDrill(ch ? [{ slug: ch.slug, name: ch.name }] : []);
  const pushDrill = (slug: string, name: string) =>
    setDrill((s) => [...s, { slug, name }]);
  const popDrill = () => setDrill((s) => s.slice(0, -1));
  usePanelBackStack(drill.length, popDrill); // browser Back pops ONE drill level
  const [view, setView] = useViewMode(`${root}:explore`);
  const [loading, setLoading] = useState(true);
  const cardActions = useWorkspaceCardActions();
  const { findFirstByRef } = useWorkspaceActions();
  const { openHref } = useFullscreenNav();
  const flow = useReactFlow();

  const url = useCallback(() => {
    const p = new URLSearchParams();
    p.set('root', root);
    if (category && category !== 'all') p.set('category', category);
    if (search.trim()) p.set('q', search.trim());
    return `/api/marketplace/channels?${p.toString()}`;
  }, [root, search, category]);

  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    fetch(url(), { signal: c.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (c.signal.aborted || !d) return;
        setItems((d.items as MarketChannel[]) ?? []);
        setCategories((d.categories as MarketCategory[]) ?? []);
      })
      .catch(() => {})
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [url]);

  // The embedded channel-detail CTA (Chat / Open) postMessages us instead of
  // navigating the top window — so we drop the agent/app into the CURRENT canvas
  // in place (no workspace reload). Mirrors the card `use()` path.
  // The iframe CTA listener is gone with the iframe — ChannelPanelView's add
  // path is a direct card action, no postMessage hop.

  // Chips are the real category subchannels under the root (+ an All chip).
  const chips: FilterChip<string>[] = [
    { id: 'all', label: 'All' },
    ...categories.map((cat) => ({ id: cat.slug.slice(root.length + 1), label: cat.name })),
  ];

  // Center the camera on an instance already on the canvas (so a second "Add"
  // reveals it rather than spawning a duplicate).
  const focusExisting = (refId: string): boolean => {
    const existing = findFirstByRef(refId);
    if (!existing) return false;
    flow.setCenter(
      existing.state.x + existing.state.width / 2,
      existing.state.y + existing.state.height / 2,
      { zoom: flow.getZoom(), duration: 280 },
    );
    return true;
  };

  const use = (ch: MarketChannel) => {
    // Apps add a widget instance; agents add the agent participant. Both land on
    // the canvas (the agent node opens its chat).
    if (kind === 'widget' && ch.primaryContentId) {
      if (focusExisting(ch.primaryContentId)) return;
      cardActions?.addWidget({
        contentId: ch.primaryContentId,
        title: ch.name,
        thumbnailUrl: ch.bannerUrl ?? ch.logoUrl ?? null,
        metadata: null,
      });
      return;
    }
    if (kind === 'agent' && ch.primaryAgentId) {
      if (focusExisting(ch.primaryAgentId)) return;
      cardActions?.addAgent({
        agentId: ch.primaryAgentId,
        username: ch.slug.split('/').pop() ?? ch.name,
        displayName: ch.name,
        avatarUrl: ch.logoUrl ?? null,
      });
      return;
    }
    // No binding (shouldn't happen for a listing) — fall back to the page.
    openPage(ch);
  };

  // Clicking the card opens the app/agent CHANNEL page (the app-store detail);
  // the explicit Add button adds an instance. We use the raw channel path
  // (`/c/<slug>`) for BOTH apps and agents: the pretty `/app/…` alias works for
  // apps, but `/agent/…` is a redirect to the agent's /u profile, so the channel
  // path is the one that reliably reaches the marketplace page.
  // Open the channel as an in-panel drill-down (Back button restores the list),
  // not a fullscreen dialog — keeps the canvas in view. `openHref` is no longer
  // needed for this, but kept available for any deep-link fallbacks.
  void openHref;
  const openPage = (ch: MarketChannel) => setDetail(ch);

  const renderCard = (ch: MarketChannel) => {
    const verified = ch.verificationStatus === 'verified';
    // App-store card: cover banner (channel banner/logo if present, else the
    // deterministic gradient) with the icon overlapping, then name + blurb + Add.
    return (
      <div
        key={ch.id}
        role="button"
        tabIndex={0}
        onClick={() => openPage(ch)}
        onKeyDown={(e) => { if (e.key === 'Enter') openPage(ch); }}
        className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-border/60 bg-background transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <div className="relative h-16 w-full" style={{ background: coverGradient(ch.id, ch.color).css }}>
          {ch.bannerUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={ch.bannerUrl} alt="" className="h-full w-full object-cover" />
          )}
          {kind === 'agent' ? (
            // Agents render a round avatar on white (falls back to the colourful
            // system agent icon); apps stay square.
            <Avatar
              seed={ch.slug.split('/').pop() ?? ch.id}
              type="agent"
              src={ch.logoUrl}
              className="absolute -bottom-4 left-2.5 h-9 w-9 border-2 border-background bg-white shadow-sm"
            />
          ) : (
            <div
              className="absolute -bottom-4 left-2.5 flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border-2 border-background text-white"
              style={{ background: coverGradient(ch.id, ch.color).css }}
            >
              {/* No iconEmoji branch: seeded apps carry `lucide:<name>`
                  strings in that column, which rendered as literal text. */}
              {ch.logoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={ch.logoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <AppWindow className="h-4 w-4" strokeWidth={1.6} />
              )}
            </div>
          )}
        </div>
        <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-5">
          <div className="flex items-center gap-1">
            <span className="truncate text-[13px] font-medium">{ch.name}</span>
            {verified && (
              <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-sky-500" aria-label={t('workspace.panels.official')} />
            )}
          </div>
          <p className="mt-0.5 line-clamp-2 min-h-[28px] text-[11px] leading-snug text-muted-foreground">
            {ch.description || (ch.category ? `//${root}/${ch.category}` : `//${root}`)}
          </p>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); use(ch); }}
            className="mt-2 inline-flex items-center justify-center gap-1 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-accent"
          >
            {kind === 'agent' ? <MessageCircle className="h-3 w-3" /> : <Grid2x2Plus className="h-3 w-3" />}
            {kind === 'agent' ? t('workspace.canvas.node.chat') : t('posts.open')}
          </button>
        </div>
      </div>
    );
  };

  const renderRow = (ch: MarketChannel) => {
    const verified = ch.verificationStatus === 'verified';
    return (
      <div
        key={ch.id}
        role="button"
        tabIndex={0}
        onClick={() => openPage(ch)}
        onKeyDown={(e) => { if (e.key === 'Enter') openPage(ch); }}
        className="group flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-accent/50"
      >
        {kind === 'agent' ? (
          <Avatar
            seed={ch.slug.split('/').pop() ?? ch.id}
            type="agent"
            src={ch.logoUrl}
            className="h-8 w-8 shrink-0 border border-border/40 bg-white"
          />
        ) : (
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg text-white"
            style={{ background: coverGradient(ch.id, ch.color).css }}
          >
            {/* No iconEmoji branch — see the card above. */}
            {ch.bannerUrl || ch.logoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={(ch.bannerUrl || ch.logoUrl)!} alt="" className="h-full w-full object-cover" />
            ) : (
              <AppWindow className="h-4 w-4" strokeWidth={1.6} />
            )}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13px] font-medium">{ch.name}</span>
            {verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-sky-500" aria-label={t('workspace.panels.official')} />}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">
            {ch.description || (ch.category ? `//${root}/${ch.category}` : `//${root}`)}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); use(ch); }}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-accent"
        >
          {kind === 'agent' ? <MessageCircle className="h-3 w-3" /> : <Grid2x2Plus className="h-3 w-3" />}
          {kind === 'agent' ? t('workspace.canvas.node.chat') : t('posts.open')}
        </button>
      </div>
    );
  };

  // Card grid vs dense rows, per the per-tab toggle.
  const renderCollection = (list: MarketChannel[]) =>
    view === 'cards' ? (
      <div className={cardGridClass} style={cardGridStyle}>{list.map(renderCard)}</div>
    ) : (
      <div className="flex flex-col">{list.map(renderRow)}</div>
    );

  if (detail) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center gap-1.5 border-b border-border/60 px-2 py-1.5">
          <button
            type="button"
            onClick={popDrill}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <span className="truncate text-[13px] font-medium">{detail.name}</span>
        </div>
        {/* Rendered natively — ChannelPanelView exists to replace the
            `/c/<slug>?embed=panel` iframe: instant, shares the app's state,
            and its add path carries full metadata instead of the iframe
            CTA's metadata:null postMessage hop. */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ChannelPanelView
            slug={detail.slug}
            name={detail.name}
            canPost={false}
            onOpen={pushDrill}
            onDeleted={popDrill}
          />
        </div>
      </div>
    );
  }

  // ONE continuous list, no separate "Featured" banner. The curated apps
  // already arrive in their slots inside `items` (the ladder is applied by
  // /api/marketplace/channels, category filters included), so there is nothing
  // left to merge here.

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <FilterRail<string, string>
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={`Search ${kind === 'widget' ? 'apps' : 'agents'}…`}
        chips={chips}
        activeChip={category}
        onChipChange={setCategory}
        trailing={<ViewModeToggle mode={view} onChange={setView} />}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-muted-foreground">
            {t('workspace.panels.nothingHereYet')}
          </div>
        ) : (
          <div className="px-1">
            {renderCollection(items)}
          </div>
        )}
      </div>

      {/* The publish loop, pinned below the list. */}
      <div className="shrink-0 px-2 pb-2 pt-1.5">
        <p className="rounded-lg border border-dashed border-border/70 px-3 py-2 text-[11px] leading-snug text-muted-foreground">
          {/* Names the button as it is actually labelled ("Create"), and drops
              the `//apps` / `//agents` shorthand — neither is a path a visitor
              can go to, and it read as a broken link. */}
          {kind === 'widget'
            ? t('workspace.panels.makeYourOwnHint')
            : 'Make an agent with Create, then publish it: it lands in this catalog for everyone.'}
        </p>
      </div>
    </div>
  );
}
